import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/infra/supabase/client";
import { useToast } from "@/controllers/hooks/use-toast";
import { useDashboardData } from "@/controllers/hooks/useDashboardData";
import { useOnboardingStatus } from "@/controllers/hooks/useOnboardingStatus";
import { DashboardSidebar } from "@/ui/components/dashboard/DashboardSidebar";
import { DashboardContent } from "@/ui/components/dashboard/DashboardContent";
import { MobileDashboard } from "@/ui/components/dashboard/MobileDashboard";

import { Skeleton } from "@/ui/components/ui/skeleton";
import { useIsMobile } from "@/controllers/hooks/use-mobile";
import { useProductTour } from "@/controllers/hooks/useProductTour";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  useProductTour();
  const { data, loading, refetch } = useDashboardData();
  const { isCompleted: onboardingCompleted, loading: onboardingLoading } =
    useOnboardingStatus();

  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        navigate("/login");
        return;
      }

      // Se onboarding não foi completado, redirecionar
      if (!onboardingLoading && onboardingCompleted === false) {
        navigate("/boas-vindas");
      }
    };

    checkAuth();
  }, [navigate, onboardingCompleted, onboardingLoading]);

  const handleNavigate = (path: string) => {
    if (path === "#" || path.startsWith("#")) return;

    if (path === "/dashboard" || path === "/") {
      // Já estamos no dashboard
      return;
    }

    navigate(path);
  };

  const handleTempleClick = (temple: { id: number; status?: string }) => {
    if (temple.status === "locked") {
      toast({
        title: "Templo bloqueado",
        description: "Complete o templo anterior para desbloquear este.",
        variant: "destructive",
      });
      return;
    }

    // Navega para a página de posicionamento do templo
    navigate(`/templo/${temple.id}/posicionamento`);
  };

  const handleActionClick = (action: string) => {
    handleNavigate(action);
  };

  // Loading states
  if (loading || onboardingLoading) {
    return (
      <div className="min-h-screen bg-[#fafaf9]">
        {isMobile ? (
          <div className="p-4 space-y-6">
            <div className="bg-card p-4 rounded-lg space-y-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-4 w-1/2" />
            </div>
            <Skeleton className="h-32 w-full rounded-lg" />
            <Skeleton className="h-48 w-full rounded-lg" />
          </div>
        ) : (
          <div className="flex min-h-screen">
            <div className="w-64 bg-card border-r">
              <Skeleton className="h-full" />
            </div>
            <div className="flex-1 p-6 space-y-6">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
            <div className="w-80 bg-[#fafaf9] p-6 space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Se não há dados do usuário, mostrar erro
  if (!data.user) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Erro ao carregar dashboard
          </h2>
          <p className="text-foreground-secondary mb-4">
            Não foi possível carregar os dados do usuário.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-accent-800 text-navy-950 rounded-lg hover:bg-accent-700 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <MobileDashboard
        user={data.user}
        temples={data.temples}
        notifications={data.notifications}
        epicGoal={data.epicGoal}
        onTempleClick={handleTempleClick}
      />
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Sidebar */}
      <DashboardSidebar
        user={data.user}
        unreadNotifications={data.notifications.unread_count}
        currentPath={location.pathname}
        onNavigate={handleNavigate}
      />

      {/* Main Content */}
      <div className="ml-64 min-h-screen">
        <DashboardContent
          user={data.user}
          temples={data.temples}
          notifications={data.notifications}
          epicGoal={data.epicGoal}
          achievements={data.achievements}
          onTempleClick={handleTempleClick}
        />
      </div>
    </div>
  );
}
